/**
 * Skill View Modal — Read-only right-side drawer
 */

import React, { useState, useEffect } from 'react';
import { activeLocale } from '@/utils/locale';
import { FilePreviewModal } from '@/components/common';
import { useLanguage } from '@/hooks/useLanguage';
import { SkillsApi } from '../services/skillsApi';
import type { Skill, SkillFile } from '../types';
import './SkillViewModal.css';

interface SkillViewModalProps {
  visible: boolean;
  skill: Skill | null;
  onClose: () => void;
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(activeLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatSize(mb: number | null): string {
  if (mb == null) return '';
  if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`;
  return `${mb.toFixed(2)} MB`;
}

function FileIcon({ type }: { type?: string }) {
  if (type?.startsWith('image/')) {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
      </svg>
    );
  }
  if (type?.includes('pdf')) {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

function SectionIcon({ icon }: { icon: 'prompt' | 'files' | 'bundles' | 'triggers' | 'info' }) {
  if (icon === 'prompt') return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/>
    </svg>
  );
  if (icon === 'files') return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
  if (icon === 'bundles') return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
  if (icon === 'triggers') return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  );
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}

export const SkillViewModal: React.FC<SkillViewModalProps> = ({ visible, skill, onClose }) => {
  const { t } = useLanguage();
  const [fullSkill, setFullSkill] = useState<Skill | null>(null);
  const [files, setFiles] = useState<SkillFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ fileId?: string; fileName: string; fileType: string } | null>(null);

  useEffect(() => {
    if (!visible || !skill) { setFullSkill(null); setFiles([]); return; }
    setLoading(true);
    Promise.all([SkillsApi.getSkill(skill.skill_id), SkillsApi.getSkillFiles(skill.skill_id)])
      .then(([s, f]) => { setFullSkill(s); setFiles(f); })
      .catch(err => console.error('Error loading skill:', err))
      .finally(() => setLoading(false));
  }, [visible, skill]);

  if (!visible) return null;

  const data = fullSkill || skill;

  return (
    <div className="skill-view-overlay" onClick={e => { e.stopPropagation(); onClose(); }}>
      <div className="skill-view-drawer" onClick={e => e.stopPropagation()}>
        {/* Accent strip */}
        <div className="svm-accent-bar" />

        {/* Header */}
        <div className="svm-header">
          <div className="svm-header-icon">⚡</div>
          <div className="svm-header-body">
            <div className="svm-header-name">{data?.skill_name || '...'}</div>
            <div className="svm-header-chips">
              {data && (
                <span className={`svm-chip ${data.is_active ? 'svm-chip-active' : 'svm-chip-inactive'}`}>
                  <span className="svm-chip-dot" />
                  {data.is_active ? t('common.active') : t('common.inactive')}
                </span>
              )}
              {data?.is_shared && <span className="svm-chip svm-chip-shared">{t('common.shared')}</span>}
              {data?.skill_category && (
                <span className="svm-chip svm-chip-category">{data.skill_category}</span>
              )}
            </div>
          </div>
          <button type="button" className="svm-header-close" onClick={onClose} aria-label={t('common.close')}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="svm-loading">
            <div className="svm-spinner" />
            {t('skills.loadingSkillDetails')}
          </div>
        ) : data && (
          <>
            {/* Stats row */}
            <div className="svm-stats">
              <div className="svm-stat">
                <span className="svm-stat-value">{files.length}</span>
                <span className="svm-stat-label">{t('skills.files')}</span>
              </div>
              <div className="svm-stat">
                <span className="svm-stat-value">{data.data_bundles?.length ?? data.data_bundle_ids?.length ?? 0}</span>
                <span className="svm-stat-label">{t('skills.bundles')}</span>
              </div>
              <div className="svm-stat">
                <span className="svm-stat-value">{data.agent_count ?? 0}</span>
                <span className="svm-stat-label">{t('skills.agents')}</span>
              </div>
              <div className="svm-stat">
                <span className="svm-stat-value">{data.skill_triggers?.length ?? 0}</span>
                <span className="svm-stat-label">{t('skills.triggers')}</span>
              </div>
            </div>

            {/* Meta */}
            <div className="svm-meta">
              <div className="svm-meta-item">
                <span className="svm-meta-label">{t('skills.createdBy')}</span>
                <span className="svm-meta-value">{data.created_by_name || '—'}</span>
              </div>
              <div className="svm-meta-item">
                <span className="svm-meta-label">{t('skills.created')}</span>
                <span className="svm-meta-value">{formatDate(data.created_at)}</span>
              </div>
              <div className="svm-meta-item">
                <span className="svm-meta-label">{t('skills.lastUpdated')}</span>
                <span className="svm-meta-value">{formatDate(data.updated_at)}</span>
              </div>
            </div>

            <div className="svm-body">
              {/* Description */}
              {data.skill_description && (
                <div className="svm-section">
                  <div className="svm-section-header">
                    <span className="svm-section-icon"><SectionIcon icon="info" /></span>
                    <span className="svm-section-title">{t('skills.description')}</span>
                  </div>
                  <p className="svm-description" dir="auto">{data.skill_description}</p>
                </div>
              )}

              {/* System Prompt */}
              <div className="svm-section">
                <div className="svm-section-header">
                  <span className="svm-section-icon"><SectionIcon icon="prompt" /></span>
                  <span className="svm-section-title">{t('skills.systemPrompt')}</span>
                </div>
                {data.skill_system_prompt ? (
                  <pre className="svm-prompt">{data.skill_system_prompt}</pre>
                ) : (
                  <div className="svm-empty">{t('skills.noSystemPrompt')}</div>
                )}
              </div>

              {/* Files */}
              <div className="svm-section">
                <div className="svm-section-header">
                  <span className="svm-section-icon"><SectionIcon icon="files" /></span>
                  <span className="svm-section-title">{t('skills.files')}</span>
                  {files.length > 0 && <span className="svm-section-count">{files.length}</span>}
                </div>
                {files.length === 0 ? (
                  <div className="svm-empty">{t('skills.noFilesAttached')}</div>
                ) : files.map(f => (
                  <div
                    key={f.skill_attachment_id}
                    className="svm-file-item svm-file-clickable"
                    onClick={() => setPreviewFile({
                      fileId: f.file_id,
                      fileName: f.attachment_filename || f.file_name,
                      fileType: f.file_type || '',
                    })}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="svm-file-icon-wrap">
                      <FileIcon type={f.file_type} />
                    </div>
                    <div className="svm-file-info">
                      <div className="svm-file-name" dir="auto">{f.attachment_filename || f.file_name}</div>
                      {f.file_size_mb != null && (
                        <div className="svm-file-size">{formatSize(f.file_size_mb)}</div>
                      )}
                    </div>
                    {f.include_as_context && (
                      <span className="svm-context-chip">
                        <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        {t('projects.library.contextPill')}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Data Bundles */}
              <div className="svm-section">
                <div className="svm-section-header">
                  <span className="svm-section-icon"><SectionIcon icon="bundles" /></span>
                  <span className="svm-section-title">{t('dataBundles.title')}</span>
                  {(data.data_bundles?.length ?? 0) > 0 && (
                    <span className="svm-section-count">{data.data_bundles!.length}</span>
                  )}
                </div>
                {!data.data_bundles || data.data_bundles.length === 0 ? (
                  <div className="svm-empty">{t('skills.noBundlesAttached')}</div>
                ) : data.data_bundles.map(b => (
                  <div key={b.data_bundle_id} className="svm-bundle-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className="svm-bundle-icon-wrap"><FolderIcon /></div>
                      <div className="svm-bundle-info">
                        <div className="svm-bundle-name" dir="auto">{b.bundle_name}</div>
                        {b.bundle_description && (
                          <div className="svm-bundle-desc" dir="auto">{b.bundle_description}</div>
                        )}
                        <div className="svm-bundle-files">
                          {t('counts.files', { count: b.file_count })}
                        </div>
                      </div>
                      {b.include_as_context && (
                        <span className="svm-context-chip">
                          <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          {t('projects.library.contextPill')}
                        </span>
                      )}
                    </div>
                    {b.bundle_context_prompt && (
                      <pre className="svm-bundle-context-prompt">{b.bundle_context_prompt}</pre>
                    )}
                  </div>
                ))}
              </div>

              {/* Triggers */}
              <div className="svm-section">
                <div className="svm-section-header">
                  <span className="svm-section-icon"><SectionIcon icon="triggers" /></span>
                  <span className="svm-section-title">{t('skills.activationTriggers')}</span>
                  {(data.skill_triggers?.length ?? 0) > 0 && (
                    <span className="svm-section-count">{data.skill_triggers!.length}</span>
                  )}
                </div>
                {!data.skill_triggers || data.skill_triggers.length === 0 ? (
                  <div className="svm-empty">{t('skills.noTriggersDefined')}</div>
                ) : (
                  <div className="svm-triggers">
                    {data.skill_triggers.map(t => (
                      <span key={t} className="svm-trigger">{t}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Agent Preview - what the agent sees about this skill */}
              <div className="svm-section svm-agent-preview-section">
                <div className="svm-section-header">
                  <span className="svm-section-icon">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  </span>
                  <span className="svm-section-title">{t('skills.agentPreview')}</span>
                </div>
                <p className="svm-agent-preview-hint">{t('skills.whatTheAgentSees')}</p>
                <pre className="svm-agent-preview">{(() => {
                  const lines: string[] = [];
                  lines.push(`### ${data.skill_name}`);
                  lines.push(`**Description:** ${data.skill_description || '(none)'}`);
                  if ((data as any).activation_prompt) {
                    lines.push(`**When to activate:** ${(data as any).activation_prompt}`);
                  } else if (data.skill_triggers && data.skill_triggers.length > 0) {
                    lines.push(`**Activate when user says:** ${data.skill_triggers.map(t => `"${t}"`).join(', ')}`);
                  }
                  if (data.data_bundles && data.data_bundles.length > 0) {
                    for (const b of data.data_bundles) {
                      if (b.bundle_context_prompt) {
                        lines.push(`\n**Knowledge — ${b.bundle_name}:**`);
                        lines.push(b.bundle_context_prompt);
                      } else {
                        lines.push(`\n**Knowledge — ${b.bundle_name}:** ${b.file_count} file(s), no context prompt generated`);
                      }
                    }
                  }
                  if (files.length > 0) {
                    if (data.files_context_prompt) {
                      lines.push(`\n**Direct Files:**`);
                      lines.push(data.files_context_prompt);
                    } else {
                      lines.push(`\n**Attached files:** ${files.map(f => f.attachment_filename || f.file_name).join(', ')}`);
                    }
                  }
                  return lines.join('\n');
                })()}</pre>
              </div>
            </div>
          </>
        )}

        <div className="svm-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>{t('common.close')}</button>
        </div>
      </div>

      <FilePreviewModal
        visible={!!previewFile}
        fileId={previewFile?.fileId}
        fileName={previewFile?.fileName || ''}
        fileType={previewFile?.fileType}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
};
